import { useQuery } from "@tanstack/react-query";
import { getListReportAttachmentsUrl } from "@workspace/api-client-react";
import type { ReportAttachment } from "@workspace/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";
import { FileImage, Video, Download } from "lucide-react";

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function getFileUrl(reportId: number, attachmentId: number, token?: string) {
  const base = `/api/reports/${reportId}/attachments/${attachmentId}/file`;
  if (token) return `${base}?token=${encodeURIComponent(token)}`;
  return base;
}

interface AttachmentListProps {
  reportId: number;
  compact?: boolean;
  token?: string;
}

export default function AttachmentList({ reportId, compact = false, token }: AttachmentListProps) {
  const { data: attachments, isLoading } = useQuery<ReportAttachment[]>({
    queryKey: ["attachments", reportId, token ?? null],
    enabled: !!reportId,
    queryFn: async () => {
      const base = getListReportAttachmentsUrl(reportId);
      const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<ReportAttachment[]>;
    },
  });

  if (isLoading) {
    return (
      <div className="flex gap-2">
        <Skeleton className="h-16 w-16 rounded-lg" />
        <Skeleton className="h-16 w-16 rounded-lg" />
      </div>
    );
  }

  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="space-y-2">
      {!compact && (
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Attachments ({attachments.length})
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {attachments.map((att) => {
          const fileUrl = getFileUrl(reportId, att.id, token);
          const isImage = att.mimeType.startsWith("image/");
          const isVideo = att.mimeType.startsWith("video/");

          if (isImage) {
            return (
              <a
                key={att.id}
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-primary/50 transition-colors"
                style={{ width: compact ? 56 : 80, height: compact ? 56 : 80 }}
                title={att.filename}
              >
                <img
                  src={fileUrl}
                  alt={att.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Download className="w-4 h-4 text-white drop-shadow" />
                </div>
              </a>
            );
          }

          if (isVideo) {
            return (
              <div
                key={att.id}
                className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700"
                style={{ maxWidth: compact ? 160 : 240 }}
              >
                <video
                  src={fileUrl}
                  controls
                  preload="metadata"
                  className="block"
                  style={{ maxHeight: compact ? 80 : 120 }}
                  title={att.filename}
                />
                <div className="px-2 py-1 bg-slate-50 dark:bg-slate-800 flex items-center gap-1">
                  <Video className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="text-[10px] text-slate-500 truncate">{att.filename}</span>
                </div>
              </div>
            );
          }

          return (
            <a
              key={att.id}
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm"
              title={att.filename}
            >
              <FileImage className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="text-slate-700 dark:text-slate-300 truncate max-w-[140px]">{att.filename}</span>
              <span className="text-slate-400 text-xs shrink-0">{formatBytes(att.sizeBytes)}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}
